import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterAlt as FilterIcon,
  MoreHoriz,
  Sort as SortIcon
} from "@mui/icons-material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  timelineOppositeContentClasses
} from "@mui/lab";
import {
  Alert,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Typography
} from "@mui/material";
import {
  File,
  FileArrowDown,
  FileDoc,
  FilePdf,
  Image,
  Video
} from "@phosphor-icons/react";
import { Button, DeleteModal } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { FILTER_OPERATIONS } from "features/members";
import fileDownload from "js-file-download";
import moment from "moment";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { useCentralStore } from "stores";
import { BasicProfileInformation } from ".";
import { useDeleteMemberJournal, useMemberJournals } from "../api";
import { JournalModal } from "./JournalModal";

const recordTypeColors = {
  LIFE_EVENT: "success",
  CHURCH_ENGAGEMENT: "info",
  CHURCH_SERVICE: "warning"
};

const recordTypeOutlines = {
  LIFE_EVENT: "1px solid rgb(80, 220, 108)",
  CHURCH_ENGAGEMENT: "1px solid rgb(100, 149, 237)",
  CHURCH_SERVICE: "1px solid rgb(255, 193, 7)"
};

const recordTypeNames = {
  LIFE_EVENT: "Life Event",
  CHURCH_ENGAGEMENT: "Church Engagement",
  CHURCH_SERVICE: "Church Service"
};

export const MemberJournal = ({ handleClick, handleExport, handlePrint }) => {
  const [journalModalOpen, setJournalModalOpen] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [journalToEdit, setJournalToEdit] = useState(null);
  const open = Boolean(menuAnchorEl);
  const filterOpen = Boolean(filterAnchorEl);
  const sortOpen = Boolean(sortAnchorEl);
  const [filterType, setFilterType] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);
  const setNotification = useCentralStore(state => state.setNotification);
  const { id } = useParams();

  const deleteMemberJournalMutation = useDeleteMemberJournal();

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery);
    },
    500,
    [searchQuery]
  );

  const handleMenuClick = (event, journal) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setJournalToEdit(journal);
  };

  const handleMenuClose = event => {
    event?.stopPropagation();
    setMenuAnchorEl(null);
    // setJournalToEdit(null);
  };

  const handleEdit = event => {
    event.stopPropagation();
    handleMenuClose();
    setJournalModalOpen(true);
  };

  const handleDelete = event => {
    event.stopPropagation();
    handleMenuClose();
    openDeleteModal();
  };

  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClick = event => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const getFileIcon = attachment => {
    const extension = attachment.split(".").pop();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "webp":
        return <Image size={20} />;
      case "mp4":
      case "mov":
      case "avi":
      case "wmv":
      case "flv":
      case "mpeg":
      case "mpg":
      case "webm":
        return <Video size={20} />;
      case "pdf":
        return <FilePdf size={20} />;
      case "doc":
      case "docx":
        return <FileDoc size={20} />;
      default:
        return <File size={20} />;
    }
  };

  const { data: memberJournals, isLoading } = useMemberJournals({
    churchId: selectedChurch?.id,
    memberId: id,
    queries: {
      filter: filterType && {
        record_type: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: filterType
          }
        ]
      },
      sort: sortOrder && {
        event_date: sortOrder
      },
      search: debouncedSearchQuery
    }
  });

  const journals = useMemo(() => memberJournals || [], [memberJournals]);

  const handleFilterSelect = type => {
    setSelectedJournal(null);
    setFilterType(type);
    handleFilterClose();
  };

  const handleSortSelect = order => {
    setSelectedJournal(null);
    setSortOrder(order);
    handleSortClose();
  };

  const handleDownload = async attachment => {
    try {
      const response = await fetch(`${IMAGE_URL}/${attachment}`);
      const blob = await response.blob();

      const extension = attachment.split(".").pop();
      const filename = `Attachment-${moment().format(
        "DD-MM-YYYY-hh-mm-A"
      )}.${extension}`;

      fileDownload(blob, filename);
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to download attachment"
      });
    }
  };

  return (
    <Stack minHeight="100vh" sx={{ overflow: "hidden" }}>
      <DeleteModal
        name={journalToEdit?.event_name?.join(", ")}
        onDelete={() => {
          deleteMemberJournalMutation.mutate({
            churchId: selectedChurch?.id,
            journalId: journalToEdit?.id
          });
        }}
      />

      <BasicProfileInformation
        handleClick={handleClick}
        handleExport={handleExport}
        handlePrint={handlePrint}
      />

      {/* Tools here */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
      >
        <Stack direction="row" spacing={2} mt={2}>
          <input
            type="text"
            placeholder="Search member journal"
            style={{
              flex: 1,
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
              minWidth: "300px"
            }}
            value={searchQuery}
            onChange={e => {
              setSelectedJournal(null);
              setSearchQuery(e.target.value);
            }}
          />
          <Button
            flavor="white"
            startIcon={<FilterIcon />}
            onClick={handleFilterClick}
          >
            Filter
          </Button>
          <Menu
            anchorEl={filterAnchorEl}
            open={filterOpen}
            onClose={handleFilterClose}
          >
            <MenuItem onClick={() => handleFilterSelect(null)}>All</MenuItem>
            <MenuItem onClick={() => handleFilterSelect("CHURCH_ENGAGEMENT")}>
              Church Engagement
            </MenuItem>
            <MenuItem onClick={() => handleFilterSelect("CHURCH_SERVICE")}>
              Church Service
            </MenuItem>
            <MenuItem onClick={() => handleFilterSelect("LIFE_EVENT")}>
              Life Event
            </MenuItem>
          </Menu>
          <Button
            flavor="white"
            startIcon={<SortIcon />}
            onClick={handleSortClick}
          >
            Sort
          </Button>
          <Menu
            anchorEl={sortAnchorEl}
            open={sortOpen}
            onClose={handleSortClose}
          >
            <MenuItem onClick={() => handleSortSelect("desc")}>
              Newest to Oldest
            </MenuItem>
            <MenuItem onClick={() => handleSortSelect("asc")}>
              Oldest to Newest
            </MenuItem>
          </Menu>
          {/* Active Filters */}
          <Stack alignSelf="center" direction="row" spacing={1} mt={2}>
            {filterType && (
              <Chip
                label={recordTypeNames[filterType]}
                onDelete={() => handleFilterSelect(null)}
                color={recordTypeColors[filterType]}
                size="small"
              />
            )}
          </Stack>
        </Stack>

        <Button
          onClick={() => {
            setJournalToEdit(null);
            setJournalModalOpen(true);
          }}
        >
          Add Journal
        </Button>
      </Stack>

      {isLoading && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          mt={2}
        >
          <CircularProgress size={24} />
        </Stack>
      )}

      {!isLoading && journals.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No journals found
        </Alert>
      )}

      {!isLoading && (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems="flex-start"
          mt={2}
          spacing={{ xs: 2, lg: 0 }}
          sx={{
            flexGrow: 1,
            overflow: "hidden"
          }}
        >
          <Stack
            sx={{
              height: {
                xs: selectedJournal ? "auto" : "calc(100vh - 400px)",
                lg: "calc(100vh - 300px)"
              },
              overflow: "auto",
              width: {
                xs: "100%",
                lg: selectedJournal ? "calc(100% - 440px)" : "100%"
              },
              pr: { lg: 2 }
            }}
          >
            <Timeline
              position="right"
              sx={{
                marginBlock: 0,
                paddingTop: 0,
                px: { xs: 0, md: 1 },
                [`& .${timelineOppositeContentClasses.root}`]: {
                  flex: { xs: 0, md: 0.1 },
                  display: { xs: "none", md: "flex" }
                }
              }}
            >
              {journals.map(journal => (
                <TimelineItem key={journal.id}>
                  <TimelineOppositeContent color="text.secondary">
                    {moment(journal.event_date).format("D MMM/YYYY")}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot
                      color={recordTypeColors[journal.record_type]}
                    />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => setSelectedJournal(journal)}
                    sx={{
                      cursor: "pointer"
                    }}
                  >
                    <Stack
                      alignItems="flex-start"
                      sx={{
                        p: { xs: "12px", sm: "16px", lg: "20px" },
                        width: "100%",
                        maxWidth: { xs: "100%", sm: "600px", lg: "700px" },
                        backgroundColor: "#F8FAFC",
                        borderRadius: 2,
                        outline:
                          selectedJournal?.id === journal.id
                            ? recordTypeOutlines[journal.record_type]
                            : "none"
                      }}
                      spacing={1}
                    >
                      <Stack
                        width="100%"
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Stack
                          direction={{ xs: "column", xl: "row" }}
                          alignItems={{ xs: "flex-start", xl: "center" }}
                          spacing={{ xs: 0.5, xl: 2 }}
                          flex={1}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                              wordBreak: "break-word"
                            }}
                          >
                            {journal.event_name?.join(", ")}
                          </Typography>
                          <Chip
                            label={recordTypeNames[journal.record_type]}
                            color={recordTypeColors[journal.record_type]}
                            size="small"
                          />
                        </Stack>
                        <IconButton
                          onClick={e => handleMenuClick(e, journal)}
                          size="small"
                          sx={{ flexShrink: 0 }}
                        >
                          <MoreHoriz />
                        </IconButton>
                      </Stack>
                      <Stack
                        direction={{ xs: "column", xl: "row" }}
                        alignItems={{ xs: "flex-start", xl: "center" }}
                        spacing={{ xs: 0.5, xl: 3 }}
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                        >
                          {moment(journal.event_date).format(
                            "MMM D, YYYY • h:mm A"
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                        >
                          By: {journal.actionee}
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            wordBreak: "break-word"
                          }}
                        >
                          {journal.description || "No description"}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                      >
                        {journal.attachments?.map((attachment, index) => (
                          <Chip
                            key={attachment}
                            label={
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                              >
                                {getFileIcon(attachment)}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: { xs: "0.7rem", sm: "0.75rem" }
                                  }}
                                >
                                  {`Attachment ${index + 1}`}
                                </Typography>
                              </Stack>
                            }
                            variant="filled"
                            size="small"
                            sx={{ cursor: "pointer", mt: { xs: 0.5, sm: 0 } }}
                          />
                        ))}
                      </Stack>
                      <Stack width="100%" spacing={1}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
                        >
                          Notes
                        </Typography>
                        <Alert
                          severity="warning"
                          icon={false}
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                        >
                          {journal.note || "No notes"}
                        </Alert>
                      </Stack>
                    </Stack>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Stack>
          {selectedJournal && (
            <Stack
              alignItems="flex-start"
              sx={{
                mt: { xs: 0, lg: "6px" },
                p: { xs: "12px", sm: "16px", lg: "20px" },
                width: { xs: "100%", lg: "420px" },
                maxHeight: { lg: "calc(100vh - 300px)" },
                overflow: { lg: "auto" },
                backgroundColor: "#F8FAFC",
                borderRadius: 2
              }}
              spacing={1}
            >
              <Stack
                direction={{ xs: "column", xl: "row" }}
                alignItems={{ xs: "flex-start", xl: "center" }}
                spacing={{ xs: 0.5, xl: 2 }}
                flexWrap="wrap"
                width="100%"
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {selectedJournal.event_name?.join(", ")}
                </Typography>
                <Chip
                  label={recordTypeNames[selectedJournal.record_type]}
                  color={recordTypeColors[selectedJournal.record_type]}
                  size="small"
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={{ xs: 0.5, sm: 3 }}
                flexWrap="wrap"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                >
                  {moment(selectedJournal.event_date).format(
                    "MMM D, YYYY • h:mm A"
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                >
                  By: {selectedJournal.actionee}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
                >
                  Description
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {selectedJournal.description || "No description"}
                </Typography>
              </Stack>
              <Stack width="100%" spacing={1}>
                {selectedJournal.attachments?.map((attachment, index) => (
                  <Stack key={attachment} spacing={1}>
                    <Avatar
                      key={attachment}
                      variant="rounded"
                      src={`${IMAGE_URL}/${attachment}`}
                      alt={`Attachment ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: { xs: "180px", sm: "220px", lg: "250px" }
                      }}
                    />
                    <Button
                      flavor="neutral"
                      size="small"
                      startIcon={<FileArrowDown size={16} />}
                      sx={{
                        alignSelf: "flex-end",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" }
                      }}
                      onClick={() => handleDownload(attachment)}
                    >
                      Download
                    </Button>
                  </Stack>
                ))}
              </Stack>
              <Stack width="100%" spacing={1}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
                >
                  Notes
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                >
                  {selectedJournal.note || "No notes"}
                </Alert>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}

      <JournalModal
        memberId={id}
        open={journalModalOpen}
        onClose={() => {
          setJournalModalOpen(false);
          setJournalToEdit(null);
        }}
        initialData={journalToEdit}
      />

      <Popover
        anchorEl={menuAnchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 2
        }}
      >
        <Stack
          sx={{
            minWidth: "200px",
            backgroundColor: "white",
            p: 1,
            gap: 1
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              p: 1,
              cursor: "pointer",
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "neutral.main"
              }
            }}
            onClick={handleEdit}
          >
            <EditIcon fontSize="small" />
            <Typography variant="subtitle2">Edit</Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              p: 1,
              cursor: "pointer",
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "neutral.main"
              }
            }}
            onClick={handleDelete}
          >
            <DeleteIcon fontSize="small" />
            <Typography variant="subtitle2">Delete</Typography>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};
